import * as z from 'zod';

export const AuditLogEventType = z.union([
  z.literal('loanCreation'),
  z.literal('flagAccount'),
  z.literal('updateStopSettings'),
  z.literal('addAccountComment'), // deprecated

  z.literal('modifyMaturityDate'),
  z.literal('updateMonthlyInterestRate'),
  z.literal('modifyPropertyAddress'),
  z.literal('createPropertyAddress'),
  z.literal('deletePropertyAddress'),
  z.literal('addAppraisal'),
  z.literal('addAppraisalOrder'),
  z.literal('addInspection'),
  z.literal('addInspectionOrder'),

  z.literal('setStatus'),
  z.literal('setLoanStatusForeclosure'),
  z.literal('closeForeclosure'),
  z.literal('reopenForeclosure'),
  z.literal('pauseForeclosure'),
  z.literal('recordReo'),
  z.literal('recordLiquidation'),
  z.literal('liquidateLoan'),
  z.literal('reverseLiquidation'),
  z.literal('moveToTransfer'),
  z.literal('moveToPendingTransfer'),
  z.literal('updateTransferData'),

  z.literal('createFee'),
  z.literal('cancelFee'),
  z.literal('deriveLedger'),
  z.literal('updateFees'),
  z.literal('createAdvance'),
  z.literal('editAdvanceDisbursements'),
  z.literal('createMultipleFeesPayment'),
  z.literal('createMultipleAdvancesPayment'),

  z.literal('createPayment'),
  z.literal('cancelPayment'),
  z.literal('autopayFailed'),
  z.literal('createClawBackTransfer'),
  z.literal('reversePayment'),
  z.literal('reversePayoff'),
  z.literal('createDisbursement'),
  z.literal('reverseDisbursement'),
  z.literal('createDraw'),
  z.literal('reverseDraw'),
  z.literal('createAutopayment'),
  z.literal('createDeposit'),
  z.literal('createBalanceReallocation'),

  z.literal('addAdditionalBorrower'),
  z.literal('removeAdditionalBorrower'),
  z.literal('changePrimaryBorrower'),
  z.literal('editBorrower'),

  z.literal('generateNextStatement'),
  z.literal('regenerateStatement'),
  z.literal('regenerateStatementPdf'),
  z.literal('generateManualStatement'),
  z.literal('editManualStatement'),

  z.literal('enableAutopay'),
  z.literal('disableAutopay'),

  z.literal('editEscrowFields'),

  z.literal('saveDocumentPdf'),
  z.literal('updateDocumentTitle'),
  z.literal('updateDocumentBorrowerAccess'),
  z.literal('deleteDocument'),

  z.literal('createVerificationOfMortgage'),
  z.literal('createLoanExtensionAgreement'),
  z.literal('createLoanExtensionFeesDocument'),

  z.literal('createRequestedPayoff'),
  z.literal('createPayoff'),
  z.literal('expirePayoff'),
  z.literal('recordPayoff'),

  z.literal('updateEscrowDue'),
  z.literal('syncPayeeOnEscrow'),
  z.literal('updateEscrowSetting'),
  z.literal('updateEscrowCompany'),
  z.literal('createEscrowSetting'),
  z.literal('updateEscrowPayment'),
  z.literal('updateLoan'),
  z.literal('updateLoanBoardedDates'),
  z.literal('updateIdsAndRemittanceData'),
  z.literal('updateBorrower'),
  z.literal('constructionToPermanent'),
  z.literal('createWorkoutPlan'),
  z.literal('cancelWorkoutPlan'),
  z.literal('completeFlexModWorkoutPlan'),
  z.literal('createPrincipalModification'),
  z.literal('reversePrincipalModification'),
  z.literal('recordWriteoff'),

  z.literal('reallocatePayments'),

  z.literal('updateInvestorRemittanceSplit'),

  z.literal('updateDelinquentInterestRate'),
  z.literal('waiveDelinquentInterest'),
  z.literal('deferDelinquentInterest'),

  z.literal('updateBillSettings'),

  /**
   * Audit log only items
   */
  z.literal('updateLoanIds'),
  z.literal('removeBank'),
  z.literal('addBank'),
  z.literal('initiateMicrodeposits'),
  z.literal('sentMicrodepositEmail'),
  z.literal('firstLogin'),
  z.literal('isMaturing'),
  z.literal('nonPerformanceStatus'),
  z.literal('addUsioBank'),
  z.literal('deleteUsioBank'),
  z.literal('enableUsioBank'),
  z.literal('addRepayBank'),
  z.literal('deleteRepayBank'),
  z.literal('enableRepayBank'),
  z.literal('gracePastDue'),
  z.literal('updateBorrowerEconsent'),
  z.literal('editBorrowerDetails'),
  z.literal('removeFlagAccount'),
  z.literal('updateLoanPointOfContact'),

  // loan notes and calls
  /**
   * @deprecated use addLoanNote handler to upsert notes instead
   */
  z.literal('createLoanNote'),
  z.literal('bulkCreateLoanNotes'),
  z.literal('pinLoanNote'),
  z.literal('deleteLoanNote'),
  z.literal('addLoanNote'),
  z.literal('logCall'),

  // non-performing exceptions / reportable events
  z.literal('upsertReportableEvent'),
  z.literal('deleteReportableEvent'),

  // workflows
  z.literal('createLoanWorkflow'),
  z.literal('closeLoanWorkflow'),

  // authorized contacts
  z.literal('addThirdPartyContact'),
  z.literal('editThirdPartyContact'),
  z.literal('deleteThirdPartyContact'),

  // entities
  z.literal('addLoanEntity'),
  z.literal('editLoanEntity'),
  z.literal('deleteLoanEntity'),

  // bankruptcies
  z.literal('bankruptcyFiled'),
  z.literal('bankruptcyUpdated'),
  z.literal('bankruptcyProofOfClaimCreated'),
  z.literal('bankruptcyCompleted'),
  z.literal('bankruptcyClosed'),
  z.literal('bankruptcyArchived'),

  // workout plans
  z.literal('createWorkoutPlan'),
  z.literal('cancelWorkoutPlan'),
  z.literal('archiveWorkoutPlan'),
  z.literal('createWorkoutPlanFlexMod'),
  z.literal('flexModCancellation'),
  z.literal('createWorkoutPlanForbearance'),
  z.literal('extendForbearanceWorkoutPlan'),
  z.literal('scheduleForbearanceWorkoutPlanCancellation'),
  z.literal('cancelForbearanceWorkoutPlan'),
  z.literal('completeForbearanceWorkoutPlan'),
  z.literal('createRepaymentWorkoutPlan'),
  z.literal('cancelRepaymentWorkoutPlan'),
  z.literal('completeRepaymentWorkoutPlan'),
  z.literal('createPaymentDeferralWorkoutPlan'),
  z.literal('activatePaymentDeferralWorkoutPlan'),
  z.literal('cancelPaymentDeferralWorkoutPlan'),
]);
export type AuditLogEventType = z.infer<typeof AuditLogEventType>;

export const AuditLogBorrowerEmailType = z.union([
  // side effects
  z.literal('sendWelcomeEmail'),
  z.literal('sendWelcomePreActiveEmail'),
  z.literal('sendWelcomeInterestReserveEmail'),
  z.literal('sendWelcomeAutoPayEmail'),
  z.literal('sendGoodbyeEmail'),
  z.literal('sendLatePaymentEmail'),
  z.literal('sendGracePastDueEmail'),
  z.literal('sendNextStatementEmail'),
  z.literal('sendNextStatementInterestReserveEmail'),
  z.literal('sendFinalPaymentEmail'),
  z.literal('sendRevisedStatementEmail'),
  z.literal('sendPaymentCancelledEmail'),
  z.literal('sendPaymentSubmittedEmail'),
  z.literal('sendPaymentPostedEmail'),
  z.literal('sendPaymentPostedInterestReserveEmail'),
  z.literal('sendPaymentFailedEmail'),
  z.literal('sendPaymentReminderEmail'),
  z.literal('sendPaymentPastDueReminderEmail'),
  z.literal('sendRescindGoodbyeEmail'),
  z.literal('sendCorrectionEmail'),
  z.literal('sendAutopayEnabledEmail'),
  z.literal('sendAutopayDisabledEmail'),
  z.literal('sendPayoffQuoteEmail'),
  z.literal('sendNewDocumentAvailable'),
  z.literal('sendPaidInFullStatementEmail'),
  z.literal('sendPartialPayoffConfirmationEmail'),
  z.literal('sendPrincipalModificationAppliedEmail'),
  z.literal('sendPrincipalModificationCancelledEmail'),
  z.literal('sendForm1098AvailableEmail'),
  z.literal('sendEscrowAnalysisAvailableEmail'),
  z.literal('sendDefermentCancelledEmail'),
  z.literal('sendBorrowerDelinquentInterestAppliedEmail'),
  z.literal('sendLoanPortalInstructionsEmail'),
  z.literal('sendLoanIsMaturingEmail'),
  z.literal('sendBorrowerInsuranceExpiringSoonEmail'),
  z.literal('sendBorrowerInsuranceExpiredEmail'),
  z.literal('sendInterestRateAdjustmentEmail'),
]);
export const AuditLogSideEffectType = z.union([
  AuditLogBorrowerEmailType,

  // lender side effects
  z.literal('sendLenderPaymentSubmittedEmail'),
  z.literal('sendLenderPaymentCancelledEmail'),
  z.literal('sendLenderPaymentFailedEmail'),
  z.literal('sendLenderSidePaymentFailedEmail'),
  z.literal('sendLenderPaymentPostedEmail'),
  z.literal('sendLenderSweepCompleteEmail'),
  z.literal('sendLenderSweepFailedEmail'),
  z.literal('sendLenderGeneratePayoffReminderEmail'),

  // one off external emails
  z.literal('sendMortgageeLetterEmail'),
  z.literal('sendExternalPayoffQuoteEmail'),
  z.literal('sendExternalPaymentDueEmail'),

  // non-email side effects
  z.literal('updateLoan'),
  z.literal('updateBorrower'),
  z.literal('physicallyMailMonthlyStatement'),
  z.literal('physicallyMailDocument'),
]);
export type AuditLogSideEffectType = z.infer<typeof AuditLogSideEffectType>;
export type EmailAuditLogSideEffectType = Exclude<
  AuditLogSideEffectType,
  'physicallyMailMonthlyStatement' | 'physicallyMailDocument'
>;

export const AuditLogType = z.union([AuditLogEventType, AuditLogSideEffectType]);
export type AuditLogType = z.infer<typeof AuditLogType>;

/* AUDIT LOG ONLY */
export const AuditLogOnlyWithCommentTypes: AuditLogType[] = [
  'nonPerformanceStatus',
  'gracePastDue',
  'removeFlagAccount',
];

/* NON-EVENT AND NON-SIDE-EFFECT AUDIT LOG ITEMS */
export const AuditLogBulkAdditionType = z.literal('updateLoanPointOfContact'); // update to union when we have more items
export type AuditLogBulkAdditionType = z.infer<typeof AuditLogBulkAdditionType>;
